import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GitBranch, X, Save } from "lucide-react";
import { useState, useEffect } from "react";

type ConditionOperator = "equals" | "not_equals" | "contains" | "is_empty" | "is_not_empty" | "in" | "not_in";

export interface ConditionRule {
  showIf: {
    fieldId: string;
    operator: ConditionOperator;
    value: string;
  };
}

interface AvailableField {
  id: string;
  name: string;
  fieldType?: string;
  options?: { choices?: string[] } | null;
}

interface Props {
  conditions: ConditionRule | null;
  onChange: (conditions: ConditionRule | null) => void;
  availableFields: AvailableField[];
  disabled?: boolean;
  /** When provided, a Save button appears and saving is deferred until clicked. */
  onSave?: (conditions: ConditionRule | null) => void;
  isSaving?: boolean;
}

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: "equals",
  not_equals: "does not equal",
  contains: "contains",
  is_empty: "is empty",
  is_not_empty: "is not empty",
  // s156: pipe-delimited multi-value gates. `not_in` keeps a field visible by default and hides it
  // only on a listed exception — an UNSET value stays visible.
  in: "is any of",
  not_in: "is none of",
};

function buildSummary(
  conditions: ConditionRule,
  fields: AvailableField[]
): string {
  const { fieldId, operator, value } = conditions.showIf;
  const fieldName = fields.find((f) => f.id === fieldId)?.name;
  if (!fieldName) return "Show this field when the selected field is met";
  const opLabel = OPERATOR_LABELS[operator] ?? operator;
  const valuePart = ["is_empty", "is_not_empty"].includes(operator) ? "" : ` "${value}"`;
  return `Show this field when "${fieldName}" ${opLabel}${valuePart}`;
}

export function ConditionRuleEditor({
  conditions,
  onChange,
  availableFields,
  disabled,
  onSave,
  isSaving,
}: Props) {
  // Local draft when onSave is provided (deferred-save mode)
  const [draft, setDraft] = useState<ConditionRule | null>(conditions);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setDraft(conditions);
    setIsDirty(false);
  }, [conditions]);

  const effective = onSave ? draft : conditions;

  const handleChange = (next: ConditionRule | null) => {
    if (onSave) {
      setDraft(next);
      setIsDirty(true);
    } else {
      onChange(next);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(draft);
      setIsDirty(false);
    }
  };

  const needsValue =
    effective !== null &&
    !["is_empty", "is_not_empty"].includes(effective.showIf.operator);

  const handleEnable = () => {
    handleChange({
      showIf: {
        fieldId: availableFields[0]?.id ?? "",
        operator: "equals",
        value: "",
      },
    });
  };

  const handleClear = () => handleChange(null);

  const update = (patch: Partial<ConditionRule["showIf"]>) => {
    if (!effective) return;
    // s234 (D-234.37): editing the first test keeps any `anyOf` alternatives the spec declared.
    handleChange({ ...effective, showIf: { ...effective.showIf, ...patch } });
  };

  // Find the watched field to determine whether to show a select for its value
  const watchedField = effective
    ? availableFields.find((f) => f.id === effective.showIf.fieldId)
    : null;
  const watchedChoices =
    watchedField?.fieldType === "select"
      ? (watchedField.options?.choices ?? [])
      : [];
  const useSelectForValue = needsValue && watchedChoices.length > 0;

  if (!effective) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          No rule set — this field always shows
        </p>
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleEnable}
            className="gap-1.5"
            data-testid="button-add-condition"
          >
            <GitBranch className="h-3.5 w-3.5" />
            Add Condition
          </Button>
        )}
      </div>
    );
  }

  const { fieldId, operator, value } = effective.showIf;
  const summary = fieldId ? buildSummary(effective, availableFields) : null;

  return (
    <div
      className="rounded-lg border p-4 space-y-3"
      style={{
        backgroundColor: 'hsl(168 76% 42% / 0.06)',
        borderColor: 'hsl(168 76% 42% / 0.35)',
      }}
    >
      {/* Plain-English summary */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <GitBranch className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#1ABC9C' }} />
          {summary ? (
            <p className="text-sm font-medium leading-snug" style={{ color: '#2C3E50' }}>
              {summary}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              Select a field below to complete the rule
            </p>
          )}
        </div>
        {!disabled && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0"
            onClick={handleClear}
            title="Remove rule"
            data-testid="button-remove-condition"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Rule builder controls */}
      {!disabled && (
        <div className="space-y-3 pt-1">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#2C3E50' }}>
            Show this field when:
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            {/* Watch field */}
            <Select
              value={fieldId}
              onValueChange={(v) => update({ fieldId: v, value: "" })}
            >
              <SelectTrigger
                className="h-8 text-sm flex-1 min-w-[140px]"
                data-testid="select-condition-field"
              >
                <SelectValue placeholder="Select field…" />
              </SelectTrigger>
              <SelectContent>
                {availableFields.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Operator */}
            <Select
              value={operator}
              onValueChange={(v) =>
                update({
                  operator: v as ConditionOperator,
                  value: ["is_empty", "is_not_empty"].includes(v) ? "" : value,
                })
              }
            >
              <SelectTrigger
                className="h-8 text-sm w-[168px]"
                data-testid="select-condition-operator"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="equals">equals</SelectItem>
                <SelectItem value="not_equals">does not equal</SelectItem>
                <SelectItem value="contains">contains</SelectItem>
                <SelectItem value="is_empty">is empty</SelectItem>
                <SelectItem value="is_not_empty">is not empty</SelectItem>
                {/* s156: pipe-delimited multi-value gates. `in` = show when it matches ANY listed option;
                    `not_in` = show UNLESS it matches one (an UNSET value stays VISIBLE). */}
                <SelectItem value="in">is any of (A|B|C)</SelectItem>
                <SelectItem value="not_in">is none of (A|B|C)</SelectItem>
              </SelectContent>
            </Select>

            {/* Value — select dropdown if watched field is a select type, otherwise text */}
            {needsValue && (
              useSelectForValue ? (
                <Select
                  value={value}
                  onValueChange={(v) => update({ value: v })}
                >
                  <SelectTrigger
                    className="h-8 text-sm flex-1 min-w-[120px]"
                    data-testid="select-condition-value"
                  >
                    <SelectValue placeholder="Choose value…" />
                  </SelectTrigger>
                  <SelectContent>
                    {watchedChoices.map((choice) => (
                      <SelectItem key={choice} value={choice}>
                        {choice}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  className="h-8 text-sm flex-1 min-w-[100px]"
                  value={value}
                  onChange={(e) => update({ value: e.target.value })}
                  placeholder="Value…"
                  data-testid="input-condition-value"
                />
              )
            )}
          </div>

          {/* Save button row — only shown in deferred-save mode */}
          {onSave && (
            <div className="flex justify-end pt-1">
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                disabled={!isDirty || isSaving}
                className="gap-1.5"
                style={{ backgroundColor: '#1ABC9C', color: '#fff' }}
                data-testid="button-save-condition-rule"
              >
                <Save className="h-3.5 w-3.5" />
                {isSaving ? "Saving…" : "Save Rule"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
